import React, { useCallback, useMemo, useRef, useState } from 'react';
import { type CellContext, type ColumnDef } from '@tanstack/react-table';
import { Alert, InlineEditRowActions, Input, LoadingSpinner, Table, TextLink, Tooltip } from '@shared/components';
import { useInlineRowEdit } from '@shared/hooks/useInlineRowEdit';
import { PermissionGuard } from '@/features/auth';
import { apiErrorMessage } from '@shared/utils/apiErrorMessage';
import { Trash2Icon, PencilIcon, Loader2Icon } from 'lucide-react';
import { twoPlaceMoney } from '../utils/twoPlaceMoney';
import { formatUnitPriceNet } from '@/shared/utils/formatUnitPriceNet';
import type { MoneyJson } from '@features/products/types/products.types';

export interface ServiceItemRecord {
    id: string;
    name: string;
    code?: string | null;
    description?: string | null;
    pivot: {
        unit_quantity: number;
        unit_price_net: string;
        unit_price_net_money?: MoneyJson;
    };
}

interface Props {
    data: ServiceItemRecord[];
    onRemove: (itemId: string) => void;
    onUpdate: (itemId: string, qty: number, unitPriceNet: string) => Promise<void>;
    isLoading?: boolean;
    removingItemId?: string | null;
    /** Total-mode services price the whole visit, not each item (D5/D6): items are
     *  descriptive only, so the price column and its editor are hidden. */
    isTotalMode?: boolean;
}

export const ServiceItemDataTable: React.FC<Props> = ({
    data,
    onRemove,
    onUpdate,
    isLoading = false,
    removingItemId = null,
    isTotalMode = false,
}) => {
    const inlineEdit = useInlineRowEdit<string>();
    const [editError, setEditError] = useState<string | null>(null);
    // Refs so edits don't trigger re-renders (which would remount inputs and lose focus)
    const editQtyRef   = useRef<number>(1);
    const editPriceRef = useRef<string>('0.00');

    const startEdit = useCallback((record: ServiceItemRecord) => {
        setEditError(null);
        editQtyRef.current   = record.pivot.unit_quantity;
        editPriceRef.current = twoPlaceMoney(record.pivot.unit_price_net) ?? '0.00';
        inlineEdit.beginEdit(record.id);
        // `beginEdit` is stable; the wider `inlineEdit` object churns every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inlineEdit.beginEdit]);

    const cancelEdit = useCallback(() => {
        inlineEdit.cancelEdit();
        setEditError(null);
        // `cancelEdit` is stable; the wider `inlineEdit` object churns every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inlineEdit.cancelEdit]);

    const saveEdit = useCallback(() => {
        const id = inlineEdit.editingId;
        if (!id) return;
        setEditError(null);
        // Keep the row open (with the pending indicator up) until the server confirms;
        // a rejected save re-surfaces the error and leaves the row open to retry.
        inlineEdit
            .commit(() => onUpdate(id, editQtyRef.current, editPriceRef.current))
            .catch((err) => setEditError(apiErrorMessage(err, 'Failed to update item')));
        // `commit` is stable; the wider `inlineEdit` object churns every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inlineEdit.editingId, inlineEdit.commit, onUpdate]);

    // Read isPending via `meta`, not a columns dep: recomputing `columns` remounts
    // the open editor (see the `Table` meta prop).
    const isRowUpdating = (info: CellContext<ServiceItemRecord, unknown>, rowId: string) =>
        inlineEdit.editingId === rowId
        && !!(info.table.options.meta as { isPending?: boolean } | undefined)?.isPending;

    const columns = useMemo<ColumnDef<ServiceItemRecord>[]>(() => [
        {
            accessorKey: 'name',
            header: 'Name',
            cell: (info) => {
                const busy = isRowUpdating(info, info.row.original.id) || removingItemId === info.row.original.id;
                return <span className={`font-semibold text-sm${busy ? ' opacity-40' : ''}`}>{info.getValue() as string}</span>;
            },
        },
        {
            accessorKey: 'code',
            header: 'Code',
            cell: (info) => {
                const busy = isRowUpdating(info, info.row.original.id) || removingItemId === info.row.original.id;
                return <span className={`font-mono text-sm${busy ? ' opacity-40' : ''}`}>{(info.getValue() as string | null) || '—'}</span>;
            },
        },
        {
            accessorKey: 'description',
            header: 'Description',
            cell: (info) => {
                const busy = isRowUpdating(info, info.row.original.id) || removingItemId === info.row.original.id;
                return <span className={`text-sm${busy ? ' opacity-40' : ''}`}>{(info.getValue() as string | null) || '—'}</span>;
            },
        },
        {
            id: 'unit_quantity',
            header: 'Qty',
            cell: (info) => {
                const row = info.row.original;
                const busy = isRowUpdating(info, row.id) || removingItemId === row.id;
                if (inlineEdit.editingId === row.id) {
                    return (
                        <div className="w-24">
                            <Input
                                key={`qty-${row.id}`}
                                aria-label={`Quantity for ${row.name}`}
                                type="number"
                                min="1"
                                defaultValue={editQtyRef.current}
                                onChange={(e) => { editQtyRef.current = parseInt(e.target.value, 10) || 1; }}
                                onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                                disabled={busy}
                                autoFocus
                            />
                        </div>
                    );
                }
                return <span className={`text-sm${busy ? ' opacity-40' : ''}`}>{row.pivot.unit_quantity}</span>;
            },
        },
        ...(isTotalMode ? [] : [{
            id: 'unit_price_net',
            header: 'Unit Net (£)',
            cell: (info: CellContext<ServiceItemRecord, unknown>) => {
                const row = info.row.original;
                const busy = isRowUpdating(info, row.id) || removingItemId === row.id;
                if (inlineEdit.editingId === row.id) {
                    return (
                        <div className="w-32">
                            <Input
                                key={`price-${row.id}`}
                                aria-label={`Unit net price for ${row.name}`}
                                type="number"
                                min="0"
                                step={0.0001}
                                defaultValue={editPriceRef.current}
                                onChange={(e) => { editPriceRef.current = e.target.value.trim() || '0.00'; }}
                                onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                                disabled={busy}
                            />
                        </div>
                    );
                }
                return (
                    <span className={`text-sm${busy ? ' opacity-40' : ''}`}>
                        {formatUnitPriceNet(row, 'GBP')}
                    </span>
                );
            },
        } as ColumnDef<ServiceItemRecord>]),
        {
            id: 'actions',
            header: 'Actions',
            enableSorting: false,
            cell: (info) => {
                const row = info.row.original;
                const isRemoving = removingItemId === row.id;

                if (isRemoving) {
                    return <Loader2Icon size={18} className="animate-spin text-mp-muted" />;
                }

                if (inlineEdit.editingId === row.id) {
                    return (
                        <InlineEditRowActions
                            isPending={isRowUpdating(info, row.id)}
                            onAccept={saveEdit}
                            onCancel={cancelEdit}
                        />
                    );
                }

                const meta = info.table.options.meta as { isPending?: boolean } | undefined;
                const anyBusy = !!meta?.isPending || removingItemId !== null;
                const editLabel = isTotalMode ? 'Edit qty' : 'Edit qty / price';
                return (
                    <PermissionGuard permission={{ resource: 'smr', action: 'service.edit' }}>
                        <div className="flex space-x-1">
                            <Tooltip content={editLabel}>
                                <TextLink color="secondary" ariaLabel={editLabel} onClick={() => startEdit(row)} disabled={anyBusy} startIcon={<PencilIcon size={18} />} />
                            </Tooltip>
                            <Tooltip content="Remove item">
                                <TextLink color="error" ariaLabel="Remove item" onClick={() => onRemove(row.id)} disabled={anyBusy} startIcon={<Trash2Icon size={18} />} />
                            </Tooltip>
                        </div>
                    </PermissionGuard>
                );
            },
        },
    // `isRowUpdating` reads `meta` live at call time and isn't itself memo-stable;
    // listing it here would rebuild `columns` (and remount the open editor) every tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [inlineEdit.editingId, removingItemId, onRemove, saveEdit, startEdit, cancelEdit, isTotalMode]);

    if (isLoading) {
        return <LoadingSpinner size="lg" className="py-8" message="Loading items..." />;
    }

    return (
        <div className="space-y-2">
            {editError && <Alert variant="error">{editError}</Alert>}
            <Table<ServiceItemRecord>
                data={data}
                columns={columns}
                enableSorting={false}
                meta={{ isPending: inlineEdit.isPending }}
            />
        </div>
    );
};
